// Lib
import { Plugin, PluginKey } from '@tiptap/pm/state';

// Types
import type { EditorView } from '@tiptap/pm/view';
import { TiptapNodeType } from '../tiptapTypes';

/** Keeps a little of the previous text beside the caret once a scroll correction is needed. */
export const CARET_SCROLL_MARGIN_PX = 24;

/**
 * The code block renders its content inside a `<code>` element that scrolls horizontally, so a
 * long line can put the caret outside the visible range.
 */
const getCodeBlockScroller = (view: EditorView, position: number): HTMLElement | null => {
    const { node } = view.domAtPos(position);
    const element = node instanceof Element ? node : node.parentElement;

    return element?.closest('code') ?? null;
};

/**
 * ProseMirror scrolls the caret into view by walking from the caret out to the page, so it only
 * adjusts scroll containers that contain the editor. The code block's horizontal scroller sits
 * inside the editor instead, and is therefore never corrected: the caret stays out of view
 * whenever an edit moves it across the scrolled range. Enter is the most visible case, because the
 * new line starts at the far left of a block that is still scrolled right.
 */
export const scrollCodeBlockCaretIntoView = (view: EditorView): void => {
    const { selection } = view.state;

    if (!selection.empty) return;
    if (selection.$head.parent.type.name !== TiptapNodeType.codeBlock) return;

    const scroller = getCodeBlockScroller(view, selection.head);

    if (!scroller) return;
    if (scroller.scrollWidth <= scroller.clientWidth) return;

    const caretLeft = view.coordsAtPos(selection.head).left;
    const bounds = scroller.getBoundingClientRect();

    // Scroll by the smallest amount that reveals the caret, so an indented line keeps its indent
    // visible rather than jumping the block back to column zero.
    if (caretLeft < bounds.left) {
        scroller.scrollLeft -= bounds.left - caretLeft + CARET_SCROLL_MARGIN_PX;
        return;
    }

    if (caretLeft > bounds.right) {
        scroller.scrollLeft += caretLeft - bounds.right + CARET_SCROLL_MARGIN_PX;
    }
};

export const codeBlockCaretVisibilityPluginKey = new PluginKey('codeBlockCaretVisibility');

/**
 * Corrects the code block scroller after every edit or caret move that ProseMirror has already
 * written to the DOM. Plugin views update before ProseMirror's own scroll pass, which then finds
 * the caret already in view and leaves the block alone.
 */
export const codeBlockCaretVisibilityPlugin = (): Plugin =>
    new Plugin({
        key: codeBlockCaretVisibilityPluginKey,
        view: () => ({
            update: (view, previousState) => {
                if (!view.hasFocus()) return;

                const documentChanged = view.state.doc !== previousState.doc;
                const selectionMoved = !view.state.selection.eq(previousState.selection);

                if (!documentChanged && !selectionMoved) return;

                scrollCodeBlockCaretIntoView(view);
            },
        }),
    });
